import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  GetTagsKinkinessScoreDocument,
  UserProfileFragment,
} from 'graphql/generated/graphql';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import { AuthStateHeaders } from './auth';

export const DEFAULT_KINK_SCORE = 4;

export interface KinkinessScoreState {
  userKinkinessScore: number | undefined;
}

export const initialKinkinessScoreState: KinkinessScoreState = {
  userKinkinessScore: undefined,
};

export const kinkinessScore = createSlice({
  name: 'kinkinessScore',
  initialState: initialKinkinessScoreState,
  reducers: {
    setUserKinkinessScore: (state, action: PayloadAction<number>) => {
      state.userKinkinessScore = action.payload;
    },
  },
});

export const getUserKinkinessScore = ({
  kinkinessScore,
}: {
  kinkinessScore: KinkinessScoreState;
}) => {
  return kinkinessScore.userKinkinessScore;
};

export const getTagsKinkinessScore = createAsyncThunk(
  'kinkinessScore/getTagsKinkinessScore',
  async (
    {
      tagIds,
      isAuthenticated,
      headers,
      locale,
    }: {
      tagIds: string[];
      isAuthenticated: boolean;
      headers: AuthStateHeaders;
      locale: string;
    },
    { dispatch },
  ) => {
    const queryOptions = buildGraphaelQueryOptions(
      isAuthenticated,
      headers,
      locale,
    );

    const { data } = await apolloClient.query({
      query: GetTagsKinkinessScoreDocument,
      ...queryOptions,
      variables: {
        tagsIds: tagIds,
      },
    });

    const kinkinessAverage = calculateKinkinessAverage(data.tags);

    dispatch(setUserKinkinessScore(Math.round(kinkinessAverage)));
  },
);

export const calculateKinkinessScore = createAsyncThunk<
  void,
  {
    user?: UserProfileFragment;
    isAuthenticated: boolean;
    headers: AuthStateHeaders;
    locale: string;
  },
  {
    state: {
      kinkinessScore: KinkinessScoreState;
    };
  }
>(
  'kinkinessScore/calculateKinkinessScore',
  async (
    {
      user,
      isAuthenticated,
      headers,
      locale,
    }: {
      user: UserProfileFragment;
      isAuthenticated: boolean;
      headers: AuthStateHeaders;
      locale: string;
    },
    { dispatch, getState },
  ) => {
    if (getState().kinkinessScore.userKinkinessScore !== undefined) {
      return;
    }

    if (user && user.modeKinkiness != null && user.modeKinkiness > 0) {
      dispatch(setUserKinkinessScore(user.modeKinkiness));
    } else if (
      user &&
      user.onboardingResponses != null &&
      user.onboardingResponses.length > 0
    ) {
      const { onboardingResponses } = user;
      const tagIds = onboardingResponses[0]?.responses.tagIds;

      if (!tagIds) {
        dispatch(setUserKinkinessScore(DEFAULT_KINK_SCORE));
        return;
      }

      dispatch(
        getTagsKinkinessScore({ tagIds, isAuthenticated, headers, locale }),
      );
    } else {
      dispatch(setUserKinkinessScore(DEFAULT_KINK_SCORE));
    }
  },
);

export const calculateKinkinessAverage = (tags) => {
  const tagsWithKinkinessScore = tags.filter((tag) => tag.kinkinessScore);

  if (tagsWithKinkinessScore.length === 0) {
    return DEFAULT_KINK_SCORE;
  }

  const kinkinessScoreSum = tagsWithKinkinessScore.reduce(
    (acc, tag) => acc + tag.kinkinessScore,
    0,
  );

  const kinkinessAverage = kinkinessScoreSum / tagsWithKinkinessScore.length;

  return kinkinessAverage;
};

export const { setUserKinkinessScore } = kinkinessScore.actions;
export default kinkinessScore.reducer;
